import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Quote, QuoteStatus } from '../mocks/quotes';

const dbStatusToMock: Record<string, QuoteStatus> = {
  draft: 'Draft', sent: 'Sent', viewed: 'Sent', accepted: 'Accepted',
  rejected: 'Rejected', expired: 'Expired',
};
const mockStatusToDb: Record<QuoteStatus, string> = {
  Draft: 'draft', Sent: 'sent', Accepted: 'accepted', Rejected: 'rejected', Expired: 'expired',
};

function rowToQuote(r: Record<string, unknown>): Quote {
  return {
    id: r.id as string,
    leadId: (r.lead_id as string) || '',
    leadName: '',
    leadEmail: '',
    leadPhone: '',
    leadService: '',
    destination: (r.destination as string) || '',
    travelDate: (r.travel_date as string) || '',
    pax: (r.pax_adults as number) || 1,
    title: (r.title as string) || 'Travel Quote',
    items: [],
    subtotal: (r.subtotal as number) || 0,
    discountType: 'fixed',
    discountValue: (r.discount_amount as number) || 0,
    discountAmount: (r.discount_amount as number) || 0,
    taxRate: 0,
    taxAmount: 0,
    totalAmount: (r.total_amount as number) || 0,
    currency: (r.currency as string) || 'AED',
    validUntil: (r.valid_until as string) || '',
    status: dbStatusToMock[(r.status as string) || 'draft'] || 'Draft',
    notes: (r.notes as string) || '',
    terms: '',
    agent: 'You',
    agentAvatar: 'YO',
    createdAt: (r.created_at as string) || '',
    sentAt: null,
    respondedAt: null,
    version: 1,
  };
}

export function useQuotes() {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('quotes').select('*').order('created_at', { ascending: false });
    setQuotes((data || []).map((r) => rowToQuote(r as Record<string, unknown>)));
    setLoading(false);
  }, []);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  const createQuote = async (quote: Partial<Quote>) => {
    const num = `QT-${Date.now().toString().slice(-6)}`;
    const { data, error } = await supabase.from('quotes').insert({
      quote_number: num,
      title: quote.title,
      destination: quote.destination,
      travel_date: quote.travelDate || null,
      pax_adults: quote.pax || 1,
      status: mockStatusToDb[quote.status || 'Draft'],
      subtotal: quote.subtotal || 0,
      discount_amount: quote.discountAmount || 0,
      total_amount: quote.totalAmount || 0,
      currency: quote.currency || 'AED',
      valid_until: quote.validUntil || null,
      notes: quote.notes,
    }).select().maybeSingle();
    if (!error && data) setQuotes((prev) => [rowToQuote(data as Record<string, unknown>), ...prev]);
    return { data, error };
  };

  const updateQuoteStatus = async (id: string, status: QuoteStatus) => {
    const { error } = await supabase.from('quotes').update({ status: mockStatusToDb[status] }).eq('id', id);
    if (!error) setQuotes((prev) => prev.map((q) => q.id === id ? { ...q, status } : q));
  };

  return { quotes, loading, refetch: fetchQuotes, createQuote, updateQuoteStatus };
}
